import { ErrorPanel, SkeletonSet } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import { SettingsHeader } from './common.js';
import { visibleSettings } from './settingsNav.js';

/**
 * Settings home — the map of the Settings centre.
 *
 * Grouped cards rather than one long list: a person arriving here is looking
 * for a thing to change, and the grouping is how they find it. Each card says
 * what can actually be done on that screen, so the description does the work of
 * routing them instead of making them open every section to find out.
 */
export function SettingsIndex() {
  const navigate = useNav();
  // `usePermissions()` starts with an empty set, so `visibleSettings` returns
  // nothing and this screen used to flash "No settings available to you" at
  // every administrator on every load — a denial rendered from an answer that
  // had not arrived. `isLoading`/`isError` are the difference between "you hold
  // none" and "we have not been told yet".
  const { permissions, isLoading, isError, refetch } = usePermissions();
  const sections = visibleSettings(permissions);

  return (
    <div className="rn-col">
      <SettingsHeader
        title="Settings"
        blurb="Configure how your organization runs in Phibian — who has access, how time and location are recorded, and which systems you exchange records with."
      />

      {isLoading && sections.length === 0 ? (
        <SkeletonSet surface="table" rows={4} />
      ) : isError && sections.length === 0 ? (
        <ErrorPanel
          message="We couldn’t check what you have access to just now. This is a connection problem, not a decision about your account."
          onRetry={() => void refetch()}
        />
      ) : sections.length === 0 ? (
        // A real principal can land here with no readable sections (a worker,
        // say). That is a legitimate state, not an error, and it says who to
        // ask rather than leaving them staring at a blank page.
        <div className="rn-card" role="status">
          <h2 className="rn-card-title">No settings available to you</h2>
          <p className="rn-muted" style={{ marginBottom: 0 }}>
            Settings are granted deliberately, never by default. If you need to change something
            here, ask an administrator in your organization to grant you access.
          </p>
        </div>
      ) : (
        sections.map((section) => (
          <section key={section.key} className="rn-col" style={{ gap: 'var(--space-10)' }}>
            <div>
              <h2 className="rn-card-title" style={{ fontSize: '1rem' }}>
                {section.label}
              </h2>
              <p className="rn-muted" style={{ marginTop: 'var(--space-4)' }}>
                {section.description}
              </p>
            </div>
            <div className="set-grid">
              {section.items.map((item) => (
                <a
                  key={item.key}
                  className="set-card"
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.href);
                  }}
                >
                  <span className="set-card-title">{item.label}</span>
                  <span className="set-card-desc">{item.description}</span>
                </a>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
